<template>
  <div class="v-tour-container shortkey-container">
    <v-tour :name="name + ran" ref="tour" :steps="data" :callbacks="options">
      <template v-slot:default="tour">
        <svg data-v-8e195e12="" v-show="open" class="tour-guide">
          <path data-v-8e195e12="" :d="background" class="region--highlight"></path>
        </svg>

        <v-step
          v-if="tour.steps[tour.currentStep]"
          ref="step"
          :key="tour.currentStep"
          :step="tour.steps[tour.currentStep]"
          :previous-step="tour.previousStep"
          :next-step="tour.nextStep"
          :stop="tour.stop"
          :skip="tour.skip"
          :is-first="tour.isFirst"
          :is-last="tour.isLast"
          :labels="tour.labels"
          :enabled-buttons="options.enabledButtons"
          :highlight="options.highlight"
          :style="{
            width: (tour.steps[tour.currentStep].width || 400) + 'px',
            minHeight: (tour.steps[tour.currentStep].minHeight || 150) + 'px',
          }"
          @targetNotFound="targetNotFound"
        >
          <!-- Header -->
          <template v-slot:header>
            <div class="v-step__header">
              <div class="step-title">
                {{ tour.steps[tour.currentStep].header?.title }}
              </div>

              <div class="button icon24 close" @click="close" shortkey-target="Close"></div>
            </div>
          </template>

          <!-- Content -->
          <template v-slot:content>
            <div class="v-step__content">
              <div
                class="step-text"
                v-html="
                  isProcessXss
                    ? $ms.commonFn.processXssValue(tour.steps[tour.currentStep].content)
                    : tour.steps[tour.currentStep].content
                "
              ></div>
            </div>
          </template>

          <!-- Footer -->
          <template v-slot:actions>
            <div class="v-step__buttons">
              <div class="left-footer">
                <div v-if="isShowStep" class="step-number">{{ tour.currentStep + 1 }}/{{ tour.steps.length }}</div>
              </div>
              <div class="right-footer">
                <ms-button v-if="tour.isLast" :text="textBtnStop" type="primary" @click="tour.stop"></ms-button>

                <ms-button v-if="!tour.isLast" :text="textBtnNext" type="primary" @click="tour.nextStep"></ms-button>

                <ms-button
                  class="outline-button btn-back"
                  v-if="!tour.isFirst"
                  :text="textBtnBack"
                  hasBorder
                  @click="tour.previousStep"
                ></ms-button>
              </div>
            </div>
          </template>
        </v-step>
      </template>
    </v-tour>

    <span ref="shortkey" class="popup-shortkey" @shortkey="shortkeyAction" v-shortkey="defaultShortKey"></span>
  </div>
</template>

<script>
import {getCurrentInstance, onMounted, watch, defineComponent, computed, ref, nextTick} from 'vue';
import componentUtil from '@/commons/componentUtil';
import windowResize from '@/commons/windowResize';
import {shortkeyStatusbar} from '@/mixins/common/shortkeyStatusbar';
import EventBusGlobal, {GlobalEventName} from '@/commons/eventBusGlobal';

export default defineComponent({
  mixins: [shortkeyStatusbar],
  props: {
    /** Tên */
    name: {
      default: '',
      type: String,
    },
    /** Dữ liệu step */
    steps: {
      default: [],
      type: Array,
    },
    /** Trạng thái đóng mở */
    open: {
      default: false,
      type: Boolean,
    },
    /** Option custom */
    option: {
      default: {},
      type: Object,
    },
    /** Index step hiện tại */
    currentIndex: {
      default: -1,
      type: Number,
    },
    /** Container scope */
    container: {
      default: '',
      type: String,
    },
    /** Padding  */
    padding: {
      default: {top: 4, left: 8},
      type: Object,
    },
    filterShow: {
      default: false,
      type: Boolean,
    },
    //Text button Start
    textBtnStop: {
      default: 'Bắt đầu',
      type: String,
    },
    //Text button Next
    textBtnNext: {
      default: 'Tiếp theo',
      type: String,
    },
    //Text button Back
    textBtnBack: {
      default: 'Quay lại',
      type: String,
    },
    //Có hiển thị text các bước không?
    isShowStep: {
      default: true,
      type: Boolean,
    },
    //Có xử lý XSS content truyền vào không?
    isProcessXss: {
      default: true,
      type: Boolean,
    },
  },
  setup(props, {emit}) {
    const {proxy} = getCurrentInstance();
    const ran = ref(parseInt(Math.random() * 10000));

    /** Dữ liệu step custom */
    const data = computed(() =>
      proxy.steps
        .map(x => ({
          ...x,
          target: [proxy.container, x.target].filter(x => x).join(' '),
          params: {enableScrolling: !x.scroll, ...(x.params || {})},
          item: x,
        }))
        .filter(x => !x.hide),
    );

    /** window width, height */
    const {width, height} = windowResize.resizeWindow();

    const position = ref({
      top: 0,
      width: 0,
      left: 0,
      height: 0,
    });

    /** Position focus control background */
    const background = computed(() =>
      [
        'M0 0 V',
        proxy.height,
        'H',
        proxy.width,
        'V 0 Z',
        `M${proxy.position.left},${proxy.position.top}`,
        'v',
        proxy.position.height,
        'h',
        proxy.position.width,
        'v',
        `-${proxy.position.height}z`,
      ].join(' '),
    );

    onMounted(() => {
      /** Lưu proxy tour */
      proxy.tour = proxy.$refs.tour;
    });

    watch(
      () => proxy.open,
      newVal => {
        /** Đóng mở step */
        newVal ? proxy.start() : proxy.close();
        if(!newVal) {
          // raise KBĐT notification
          EventBusGlobal.$emit(GlobalEventName.raiseKBDTNotification);
        }
      },
    );

    /**
     * Bắt đầu show
     */
    function start() {
      if (proxy.steps.length) {
        emit('update:open', true);
        proxy.$ms.commonFn.shortkeyPushView(proxy.$el);

        proxy.tour.start();
      } else {
        emit('update:open', false);
      }
    }

    /**
     * Đóng step
     */
    function close() {
      proxy.$ms.commonFn.shortkeyPopView(proxy.$el);
      proxy.tour.stop();
    }

    /** Current step */
    const currentStep = computed(() => proxy.tour?.currentStep);

    onMounted(() => {
      watch(
        () => proxy.currentStep,
        newVal => {
          let step = proxy.data[newVal];

          if (step) {
            emit('update:currentIndex', newVal);
            emit('changeStep', newVal, step);

            let func = function (ele, step) {
              if (ele) {
                let pos = ele.getBoundingClientRect();
                //Fix riêng cho các button có padding left
                var isCustom = step?.header?.title == 'Chức năng Hướng dẫn' || step?.header?.title == 'Phím tắt';
                if (!isCustom) {
                  proxy.position = {
                    top: pos.top - (proxy.padding.top || 0) - (step.position?.top || 0),
                    left: pos.left - (proxy.padding.left || 0) - (step.position?.left || 0),
                    height: pos.height + 2 * (proxy.padding.top || 0) + 2 * (step.position?.top || 0),
                    width: pos.width + 2 * (proxy.padding.left || 0) + 2 * (step.position?.left || 0),
                  };
                } else {
                  proxy.position = {
                    top: pos.top - (proxy.padding.top || 0),
                    left: pos.left - 16,
                    height: pos.height + 2 * (proxy.padding.top || 0),
                    width: pos.width + 2 * (proxy.padding.left || 0),
                  };
                }
              }
            };

            let element = document.querySelector(step.target);

            nextTick(() => {
              element = element || proxy.$refs.step?.targetElement;
              if (step.delay > 0) {
                setTimeout(() => func(element, step), step.delay);
              } else {
                func(element, step);
              }
            });
          }
        },
      );
    });

    function hideStep() {
      if (proxy.filterShow) {
        proxy.data.forEach(x => (x.item.hide = !document.querySelector(x.target)));
      }
    }

    /** Default option */
    const defCallback = {
      onStop: (...args) => {
        emit('update:open', false);
        proxy.option?.onStop && proxy.option.onStop(...args);
      },
      onStart: function (index) {
        let step = proxy.steps[0];
        proxy.scrollTarget(step);
        hideStep();
        proxy.option?.onStart && proxy.option.onStart(index);
      },
      onNextStep: function (index) {
        let step = proxy.steps[index + 1];
        proxy.scrollTarget(step);
        proxy.option?.onNextStep && proxy.option.onNextStep(index);
      },
      onPreviousStep: function (index) {
        let step = proxy.steps[index - 1];
        proxy.scrollTarget(step);
        proxy.option?.onPreviousStep && proxy.option.onPreviousStep(index);
      },
    };

    const options = computed(() => ({...props.option, ...defCallback}));

    function scrollTarget(step) {
      step.scroll && componentUtil.scrollInto(document.querySelector(step.target), step.scroll);
    }

    let defaultShortKey = {
      Close: ['esc'], //Đóng
    };

    /**
     * emit event phím tắt
     */
    const shortkeyAction = e => {
      let me = this;
      switch (e.srcKey) {
        case 'Close':
          proxy.close();
          e.stopPropagation();
          e.preventDefault();
          break;

        default:
          emit('shortkeyAction', e);
          break;
      }
    };

    /**
     * Xử lý next step khi không có target
     */
    function targetNotFound(step) {
      let index = proxy.data.indexOf(step);
      if (index == proxy.steps.length - 1) {
        proxy.close();
      } else {
        var e = event;
        nextTick(() => {
          if (e?.which == 37 || e.target.classList.contains('btn-back')) {
            proxy.tour.previousStep();
          } else {
            proxy.tour.nextStep();
          }
        });
      }
    }

    return {
      start,
      close,
      options,
      currentStep,
      data,
      background,
      width,
      height,
      scrollTarget,
      position,
      ran,
      defaultShortKey,
      shortkeyAction,
      targetNotFound,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/tour/MsTour.scss';
</style>

<style lang="scss">
.v-tour__target--highlighted {
  -webkit-box-shadow: none;
  box-shadow: none;
  background-color: #fff;
  z-index: unset;
}
.step-text {
  a {
    color: $blue;
    cursor: pointer;
  }
}
</style>
